/**
 * Payment Sources API
 * CRUD operations for payment methods
 */

import { browserContext } from '../utils/platform';
import { apiRequest } from './core';
import type { PaymentMethodKind, RiskModelConfig } from '../types';

// ============================================================================
// Request Types
// ============================================================================

interface CombinedPaymentSourcesRequest {
  amount_to_pay: number;
  browser_context: string;
  rc_token?: string;
  payg_context?: boolean;
  currency?: string;
  fx_rate?: number;
  credits_requested?: number;
  [key: string]: string | number | boolean | undefined;
}

interface AddPaymentSourceRequest {
  method: PaymentMethodKind;
  browser_context: string;
  [key: string]: string | PaymentMethodKind | undefined;
}

interface SetDefaultRequest {
  set_default: boolean;
  browser_context: string;
  [key: string]: string | boolean | undefined;
}

interface UpdatePaymentSourceRequest {
  context: {
    cardData: any;
    name: string;
    zip: string;
  };
  browser_context: string;
  [key: string]: string | { cardData: any; name: string; zip: string } | undefined;
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Payment source item from API response.
 * Used across /combined (current, existing) and /payment_sources (options) endpoints.
 * All fields optional to accommodate different response contexts.
 */
export interface ApiPaymentSource {
  id?: number;
  kind: string;
  human_kind?: string;
  context?: any;
  error_text?: string | null;
  gateway_name?: string;
  risk_model?: string | null;
  risk_model_config?: RiskModelConfig | null;
  /** Backend returns camelCase for this field */
  requiresConfiguration?: boolean;
  requires_pay_as_you_go_validation?: boolean;
  eligible_to_pay_as_you_go?: boolean;
  full_name?: string;
  expired?: boolean;
  is_default?: boolean;
  is_duplicate?: boolean;
  enabled?: boolean;
  reauthorize_required?: boolean;
  can_be_used?: boolean;
  /** Present in /combined `current` array — true for "add new" options, not actual sources */
  adding?: boolean;
  /** Extra query params to merge into the process_credits request (e.g. "force_add_kind=pp").
   *  Present on items in the `current` array when the user has no saved payment method. */
  extraParams?: string;
}

/**
 * Available payment option from API (simpler shape than ApiPaymentSource).
 * Represents a payment method kind that can be added (e.g., "Credit Card", "PayPal").
 */
export interface ApiPaymentOption {
  kind: string;
  human_kind: string;
  preferred?: boolean;
  /** Kind-specific data (e.g., Apple Pay session configuration) */
  context?: any;
}

export interface CombinedPaymentSourcesResponse {
  current: ApiPaymentSource[];
  payment_sources: {
    available?: ApiPaymentOption[];
    existing?: ApiPaymentSource[];
  };
  user: {
    client_available_credit: string;
    /** Available credit in local currency — backend may return string or float */
    local_available_credit?: number;
  };
  fees?: {
    purchase_fees?: number;
    purchase_fees_text?: string;
    amount_to_pay?: number;
    total_amount?: number;
    /** Amount to pay in local currency */
    local_amount_to_pay?: number;
    /** Total amount in local currency */
    local_total_amount?: number;
    /** Purchase fees in local currency */
    local_purchase_fees?: number;
    /** Purchase fees text in local currency */
    local_purchase_fees_text?: string;
    /** Credits requested in USD — echoed back from the request */
    credits_requested?: number;
    /** Credits requested in local currency */
    local_credits_requested?: number;
    /**
     * Authoritative exchange rate confirmed by the server.
     * Should be used for subsequent /process_credits and /user_credits calls
     * instead of the client-side fxRate prop, to avoid stale-rate errors.
     */
    fx_rate?: number;
    /** ISO currency code confirmed by the server (e.g., 'MXN') */
    currency?: string;
    [key: string]: any;
  };
}

export interface PaymentSourceConfigResponse {
  gateway_name: string;
  jsSrc?: string;
  publicKey?: string;
  pf_token?: string;
  fraud_html_url?: string;
  fraud_img_url?: string;
  payment_session?: any;
  risk_model?: string | null;
  risk_model_config?: RiskModelConfig | null;
  [key: string]: any;
}

export interface AddPaymentSourceResponse {
  source: {
    id: number;
    gateway_name: string;
  };
  options: ApiPaymentSource[];
  config?: PaymentSourceConfigResponse;
}

export interface SetDefaultResponse {
  sources: {
    existing: any[];
    available: any[];
  };
  options: any[];
}

export interface FetchPaymentSourcesResponse {
  existing: ApiPaymentSource[];
  available: ApiPaymentOption[];
}

export interface UpdatePaymentSourceResponse {
  conversion_event_id?: string;
  [key: string]: any;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch combined payment sources, user credit, and fees
 *
 * @param amountToPay - The payment amount (sent to /combined for fee calculation)
 * @param isPayg - Whether this is a PAYG context (sent as payg_context to backend)
 * @param rcToken - reCAPTCHA token
 * @param currency - ISO currency code for local-currency fee calculation (e.g., 'MXN')
 * @param fxRate - Exchange rate for local-currency fee calculation
 * @param creditsRequested - USD credit amount the user receives; backend echoes it back as local_credits_requested
 */
export const fetchCombinedPaymentSources = async (
  amountToPay: number,
  isPayg = false,
  rcToken?: string,
  currency?: string,
  fxRate?: number,
  creditsRequested?: number
): Promise<CombinedPaymentSourcesResponse> => {
  const requestBody: CombinedPaymentSourcesRequest = {
    amount_to_pay: amountToPay,
    ...browserContext,
    ...(rcToken && { rc_token: rcToken }),
    payg_context: isPayg,
    ...(currency && { currency }),
    ...(fxRate && { fx_rate: fxRate }),
    credits_requested: creditsRequested ?? amountToPay
  };

  return apiRequest<CombinedPaymentSourcesResponse>(
    '/my/payment_sources/combined',
    {
      method: 'POST',
      body: requestBody
    }
  );
};

/**
 * Add a payment method (PayPal, Google Pay, Apple Pay, or create credit card source)
 */
export const addPaymentSource = async (
  method: PaymentMethodKind
): Promise<AddPaymentSourceResponse> => {
  const requestBody: AddPaymentSourceRequest = {
    method,
    ...browserContext
  };

  return apiRequest<AddPaymentSourceResponse>(
    '/my/payment_sources',
    {
      method: 'POST',
      body: requestBody
    }
  );
};

/**
 * Set payment method as default
 */
export const setPaymentSourceAsDefault = async (
  paymentSourceId: number
): Promise<SetDefaultResponse> => {
  const requestBody: SetDefaultRequest = {
    set_default: true,
    ...browserContext
  };

  return apiRequest<SetDefaultResponse>(
    `/my/payment_sources/${ paymentSourceId }`,
    {
      method: 'PUT',
      body: requestBody
    }
  );
};

/**
 * Get payment source configuration (for initializing gateway after adding/editing)
 *
 * NOTE: This uses PUT with generate_tokens: true which generates new gateway tokens.
 * This should ONLY be used when ADDING or EDITING payment sources, NOT when processing payments.
 */
export const getPaymentSourceConfig = async (
  paymentSourceId: number
): Promise<PaymentSourceConfigResponse> => {
  const requestBody = {
    generate_tokens: true,
    ...browserContext
  };

  return apiRequest<PaymentSourceConfigResponse>(
    `/my/payment_sources/${ paymentSourceId }`,
    {
      method: 'PUT',
      body: requestBody
    }
  );
};

/**
 * Update payment method (for credit card, submit card data)
 */
export const updatePaymentSource = async (
  paymentSourceId: number,
  cardData: any,
  name: string,
  zip: string
): Promise<UpdatePaymentSourceResponse> => {
  const requestBody: UpdatePaymentSourceRequest = {
    context: {
      cardData,
      name,
      zip
    },
    ...browserContext
  };

  return apiRequest<UpdatePaymentSourceResponse>(
    `/my/payment_sources/${ paymentSourceId }`,
    {
      method: 'PUT',
      body: requestBody
    }
  );
};

/**
 * Fetch payment sources (standalone, without purchase context).
 *
 * @param paygContext - When true, sends payg_context=true so the backend
 *   returns `am` (Apple Merchant) instead of `ap` (one-time Apple Pay).
 */
export const fetchPaymentSources = async (paygContext = false): Promise<FetchPaymentSourcesResponse> => {
  const queryParams: Record<string, string> = { ...browserContext };
  if (paygContext) {
    queryParams.payg_context = 'true';
  }
  const params = new URLSearchParams(queryParams).toString();
  return apiRequest<FetchPaymentSourcesResponse>(`/my/payment_sources?${ params }`);
};

/**
 * Remove a saved payment source.
 * Returns the same structure as fetchPaymentSources (existing + available) so state can be updated without a separate GET.
 */
export const removePaymentSource = async (paymentSourceId: number): Promise<FetchPaymentSourcesResponse> => apiRequest<FetchPaymentSourcesResponse>(
  `/my/payment_sources/${ paymentSourceId }`,
  { method: 'DELETE' }
);
